import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Minus, Plus } from 'lucide-react';
import { BottomSheet } from '@shared/ui/containers/BottomSheet';
import { Button } from '@shared/ui/primitives/Button';
import { TextInputField } from '@shared/ui/forms/FormFields';
import { Card } from '@shared/ui/containers/Card';
import { Banner } from '@shared/ui/feedback/Banner';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { toast } from '@shared/ui/feedback/Toast';
import { toApiError } from '@shared/api/errors';
import { publicBaseUrl } from '@shared/lib/platform';
import { initialsOf } from '@shared/lib/format';
import { AccessInvitePropertyPicker } from './AccessInvitePropertyPicker';
import { EntryTypeGlyph } from './EntryTypeList';
import { formatAccessInviteLocationLabel } from './accessInviteLocationUi';
import { ACCESS_INVITES_QUERY_KEY } from './accessInviteCache';
import { inviteCredentialsQueryKey } from './InviteCredentialPass';
import {
  createAccessInvite,
  fetchAccessEntryTypesForInvite,
  fetchAccessInviteLocations,
  type EligibleLocation,
  type MemberAccessEntryTypeOption,
} from './api';
import { MEMBER_CONTACTS_QUERY_KEY, type Person } from './memberContactsApi';
import { missingVisitorContact, resolveVisitorContactRule } from './visitorContactRule';
import { VisitDates } from './VisitDates';
import { ANY_TYPE, entriesLabel, entryTypeRule, startOfToday } from './visitorAccessFormat';
import { chipClass } from './visitorAccessStyles';

const DAY_MS = 24 * 60 * 60 * 1000;

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

interface NewPinSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** One person, or a group who each get their own PIN for the same visit. */
  people: Person[];
  /** Called with the people who now have a PIN, once at least one was created. */
  onCreated: (people: Person[]) => void;
}

/**
 * Creates a PIN for each chosen person: pick the property and the visit day. Each PIN follows its
 * person's saved type, or for Any type the one picked for the visit. Their saved details travel with
 * it, so nothing about them is typed here except, for one person, the party size and the vehicle.
 */
export function NewPinSheet({ open, onOpenChange, people, onCreated }: NewPinSheetProps): JSX.Element {
  const single = people.length === 1;
  const title = single ? `New PIN for ${firstName(people[0]!.display_name)}` : `New PINs for ${people.length} people`;
  const description = single
    ? 'We send it to them. You can share it too.'
    : `${people.map((person) => firstName(person.display_name)).join(', ')} each get their own PIN.`;

  // The form mounts with each open, so every open starts fresh.
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title={title} description={description}>
      <div className="pb-4">
        <NewPinForm people={people} onCreated={onCreated} />
      </div>
    </BottomSheet>
  );
}

/** One person and the type their PIN follows. */
interface PersonVisit {
  person: Person;
  type: MemberAccessEntryTypeOption;
}

interface NewPinFormProps {
  people: Person[];
  onCreated: (people: Person[]) => void;
  /** Shows who the PIN is for beside their type, for New PIN where the person was just picked. */
  showPerson?: boolean;
  /** The type New PIN picked, which every PIN follows; otherwise each person's own. */
  type?: MemberAccessEntryTypeOption;
}

/** The visit itself: where, which day, and for one person the party and the car. */
export function NewPinForm({ people, onCreated, showPerson = false, type: pickedType }: NewPinFormProps): JSX.Element {
  const queryClient = useQueryClient();
  const single = people.length === 1 ? people[0] : undefined;

  // 1. Starts on today, and the person's saved vehicle.
  const [locationId, setLocationId] = useState('');
  const [visitDay, setVisitDay] = useState(startOfToday);
  const [chosenDays, setChosenDays] = useState<number>();
  const [adults, setAdults] = useState(1);
  const [minors, setMinors] = useState(0);
  const [vehicle, setVehicle] = useState(single?.vehicle_reg ?? '');
  const [visitType, setVisitType] = useState<MemberAccessEntryTypeOption>();
  const [tried, setTried] = useState(false);
  const [error, setError] = useState('');

  const typesQuery = useQuery({ queryKey: ['access-entry-types'], queryFn: fetchAccessEntryTypesForInvite });
  const locationsQuery = useQuery({ queryKey: ['access-invite-locations'], queryFn: fetchAccessInviteLocations });
  const locations = locationsQuery.data ?? [];

  // 2. The type picked in New PIN, else each person's own, else the one picked here for this visit:
  //    Any type has none, and one the member can no longer use counts as none.
  const ownType = (person: Person) => typesQuery.data?.find((row) => row.access_entry_type_id === person.access_entry_type_id);
  const asksVisitType = !pickedType && typesQuery.isSuccess && people.some((person) => !ownType(person));
  const typed = people.map((person) => ({ person, type: pickedType ?? ownType(person) ?? visitType }));
  const type = single ? typed[0]!.type : undefined;
  const capture = type?.capture_requirements;
  const sameType = new Set(typed.map(({ type }) => type)).size === 1 ? typed[0]!.type : undefined;

  // How long the PINs work: the type's usual length until the member picks another, up to its longest.
  // People of different types each keep their own type's length.
  const longest = sameType ? (sameType.max_validity_days ?? sameType.default_validity_days) : 1;
  const validDays = chosenDays ?? sameType?.default_validity_days ?? 1;
  const pickLength = longest > 1;

  // 3. The property, else the member's only one. The gate needs its code to issue the PIN.
  const location = locations.find((row) => row.location_id === locationId) ?? (locations.length === 1 ? locations[0] : undefined);

  // 4. What still stops the PINs being created. Saved details are fixed on the person's page.
  const problems = {
    visitType: asksVisitType && !visitType ? 'Pick what the visit is for.' : '',
    location: !location
      ? 'Select your property.'
      : location.location_code?.trim()
        ? ''
        : `${formatAccessInviteLocationLabel(location)} has no gate code yet. Ask management to set one.`,
    people: typesQuery.isSuccess ? peopleMissingDetails(typed, single ? vehicle : null).join(' ') : '',
  };
  const shown = (problem: string) => (tried && problem ? problem : undefined);

  // 5. One PIN per person, one after another so the gate is never asked for several at once.
  const mutation = useMutation({
    mutationFn: async ({ location, visits }: { location: EligibleLocation; visits: PersonVisit[] }) => {
      const created: Person[] = [];
      const failed: { person: Person; message: string }[] = [];

      for (const { person, type } of visits) {
        const asks = type.capture_requirements;
        try {
          const { invite, credentials } = await createAccessInvite({
            member_contact_id: person.member_contact_id,
            access_entry_type_id: type.access_entry_type_id,
            location_id: location.location_id,
            // A group gets one PIN per person: their saved plate, and a party of one.
            vehicle_reg: asks.vehicle_reg.capture ? (single ? vehicle : (person.vehicle_reg ?? '')).trim() || null : undefined,
            total_adults: asks.headcount.capture ? (single ? adults : 1) : undefined,
            total_minors: asks.headcount.capture ? (single ? minors : 0) : undefined,
            valid_from: visitDay.toISOString(),
            // Whole days from the visit day, as the server counts the longest allowed.
            ...(sameType ? { valid_until: new Date(visitDay.getTime() + validDays * DAY_MS).toISOString() } : {}),
            public_base_url: publicBaseUrl(),
          });
          if (credentials) queryClient.setQueryData(inviteCredentialsQueryKey(invite.access_invite_id), credentials);
          created.push(person);
        } catch (err) {
          failed.push({ person, message: toApiError(err).message });
        }
      }

      return { created, failed };
    },
    onSuccess: ({ created, failed }) => {
      void queryClient.invalidateQueries({ queryKey: MEMBER_CONTACTS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: ACCESS_INVITES_QUERY_KEY });

      // A timed-out request may still have created its PIN, so the member checks before trying again.
      const failureText = failed.some(({ message }) => /timeout/i.test(message))
        ? 'The gate took too long to answer. Check their page before trying again, the PIN may have been created.'
        : failed.map(({ person, message }) => `${person.display_name}: ${message}`).join(' ');

      if (!created.length) {
        setError(failureText);
        return;
      }

      toast.success(created.length === 1 ? `PIN created for ${firstName(created[0]!.display_name)}` : `${created.length} PINs created`);
      if (failed.length) toast.danger(`No PIN for ${failed.map(({ person }) => person.display_name).join(', ')}`, failureText);
      onCreated(created);
    },
  });

  const submit = () => {
    if (!location || !typesQuery.isSuccess || Object.values(problems).some(Boolean)) {
      setTried(true);
      return;
    }
    setError('');
    // No problems means everyone has a type.
    mutation.mutate({ location, visits: typed.flatMap(({ person, type }) => (type ? [{ person, type }] : [])) });
  };

  return (
    <div className="flex flex-col gap-4.5">
      {error ? <Banner variant="danger" title="No PIN was created" description={error} /> : null}

      {/* Type: each person's own, shown so the member knows which rules the PIN follows. */}
      <div className="flex flex-col gap-2.5">
        <p className="eyebrow">{showPerson ? 'For' : single ? 'Type' : 'Each person keeps their type'}</p>
        {typesQuery.isLoading ? (
          <Skeleton className="h-[4.25rem] rounded-lg" />
        ) : typesQuery.isError ? (
          <Banner variant="danger" title="Could not load visitor types" />
        ) : (
          <Card noGrain className="flex flex-col divide-y divide-hairline rounded-lg px-3.5 py-1 shadow-none">
            {typed.map(({ person, type }) => (
              <div key={person.member_contact_id} className="flex items-center gap-3 py-2.5">
                {showPerson ? <PersonAvatar name={person.display_name} /> : <EntryTypeGlyph type={type} />}
                <span className="flex min-w-0 flex-col gap-0.5 leading-tight">
                  <span className="truncate text-[0.9375rem] font-semibold text-ink">
                    {showPerson
                      ? person.display_name
                      : single
                        ? (type?.name ?? ANY_TYPE.name)
                        : `${person.display_name} · ${type?.name ?? ANY_TYPE.name}`}
                  </span>
                  <span className="text-caption text-ink-muted">
                    {!type
                      ? ANY_TYPE.rule
                      : showPerson
                        ? // The length is picked below, so only the entries rule is left to say.
                          `${type.name} · ${pickLength ? entriesLabel(type.allow_multiple_entries) : entryTypeRule(type).toLowerCase()}`
                        : pickLength
                          ? capitalize(entriesLabel(type.allow_multiple_entries))
                          : entryTypeRule(type)}
                  </span>
                </span>
              </div>
            ))}
          </Card>
        )}
        {type?.require_visitor_selfie_default ? (
          <p className="text-[0.8125rem] text-ink-secondary">They take a selfie before the PIN works.</p>
        ) : null}
      </div>

      {/* Any type: the visit picks the type its PIN follows. */}
      {asksVisitType ? (
        <div className="flex flex-col gap-2.5">
          <p className="eyebrow">What is the visit for?</p>
          <div className="flex flex-wrap gap-2">
            {typesQuery.data.map((option) => (
              <button
                key={option.access_entry_type_id}
                type="button"
                aria-pressed={option === visitType}
                className={chipClass(option === visitType)}
                onClick={() => {
                  setVisitType(option);
                  setChosenDays(undefined);
                }}
              >
                {option.name}
              </button>
            ))}
          </div>
          {shown(problems.visitType) ? <p className="text-caption text-danger">{problems.visitType}</p> : null}
        </div>
      ) : null}

      {/* Property: only asked when the member has more than one, the only one is used otherwise. */}
      {locations.length !== 1 ? (
        <div className="flex flex-col gap-2">
          <AccessInvitePropertyPicker
            locations={locations}
            selectedId={location?.location_id ?? ''}
            onSelect={setLocationId}
            isLoading={locationsQuery.isLoading}
            isError={locationsQuery.isError}
          />
          {shown(problems.location) && locations.length > 0 ? <p className="text-caption text-danger">{problems.location}</p> : null}
        </div>
      ) : shown(problems.location) ? (
        <p className="text-caption text-danger">{problems.location}</p>
      ) : null}

      {/* Visit day, and how long the PIN works when the type lets the member choose, up to its longest. */}
      <VisitDates
        dayLabel="Visit day"
        start={visitDay}
        onStart={setVisitDay}
        length={sameType && pickLength ? { unit: 'day', value: validDays, max: longest, onChange: setChosenDays } : undefined}
        days={sameType ? validDays : undefined}
      />
      {!sameType && !problems.visitType ? <p className="text-caption text-ink-muted">Each PIN lasts as long as that person's type allows.</p> : null}

      {/* This visit only, so one person: the party and the car they come in. */}
      {single && capture?.headcount.capture ? (
        <div className="flex flex-col gap-2.5">
          <p className="eyebrow">People in the car</p>
          <div className="grid grid-cols-2 gap-2.5">
            <CountStepper label="Adults" value={adults} min={1} onChange={setAdults} />
            <CountStepper label="Minors" value={minors} min={0} onChange={setMinors} />
          </div>
        </div>
      ) : null}
      {single && capture?.vehicle_reg.capture ? (
        <TextInputField
          label="Vehicle registration"
          optional={!capture.vehicle_reg.required}
          placeholder="e.g. CA 123-456"
          value={vehicle}
          onChange={(event) => setVehicle(event.target.value)}
        />
      ) : null}

      {shown(problems.people) ? <Banner variant="warning" title="Details missing" description={problems.people} /> : null}

      <div className="flex flex-col gap-2.5">
        <Button width="full" loading={mutation.isPending} onClick={submit}>
          {single ? 'Create PIN' : `Create ${people.length} PINs`}
        </Button>
      </div>
    </div>
  );
}

/**
 * What each person still lacks for their type: the contact the PIN is sent on, and a vehicle where
 * the type requires one. Any type waits for the visit's type to be picked. `vehicle` is this
 * visit's plate for one person; a group uses each person's saved one.
 */
function peopleMissingDetails(
  typed: { person: Person; type?: MemberAccessEntryTypeOption }[],
  vehicle: string | null,
): string[] {
  return typed.flatMap(({ person, type }) => {
    if (!type) return [];

    const rule = resolveVisitorContactRule(type, type.capture_requirements);
    const missing = missingVisitorContact(rule, person.phone ?? '', person.email ?? '');
    const plate = vehicle ?? person.vehicle_reg ?? '';
    const lacks = [
      missing.mobile ? 'a mobile number' : '',
      missing.email ? 'an email address' : '',
      type.capture_requirements.vehicle_reg.required && !plate.trim() ? 'a vehicle registration' : '',
    ].filter(Boolean);

    if (!lacks.length) return [];
    // A plate typed here is fixed here; a contact is fixed on the person's page.
    return [`${person.display_name} needs ${lacks.join(' and ')} for this type.`];
  });
}

interface CountStepperProps {
  label: string;
  value: number;
  min: number;
  onChange: (value: number) => void;
}

/** Compact numeric stepper for party headcount (adults / minors): the label, then minus, count, plus. */
export function CountStepper({ label, value, min, onChange }: CountStepperProps): JSX.Element {
  const clamp = (n: number) => Math.max(min, Math.min(n, 99));
  return (
    <div className="flex items-center justify-between rounded-lg border border-hairline bg-card py-1.5 pl-3.5 pr-2">
      <span className="text-body-sm text-ink">{label}</span>
      <span className="flex items-center gap-2">
        <Button variant="secondary" size="icon" aria-label={`Decrease ${label}`} onClick={() => onChange(clamp(value - 1))} disabled={value <= min}>
          <Minus className="h-4 w-4" strokeWidth={2} />
        </Button>
        <span className="min-w-4 text-center font-semibold text-ink">{value}</span>
        <Button variant="secondary" size="icon" aria-label={`Increase ${label}`} onClick={() => onChange(clamp(value + 1))}>
          <Plus className="h-4 w-4" strokeWidth={2} />
        </Button>
      </span>
    </div>
  );
}

/** A person's initials on a round tint. */
export function PersonAvatar({ name }: { name: string }): JSX.Element {
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-pill bg-primary-50 text-sm font-bold text-primary-700">
      {initialsOf(name)}
    </span>
  );
}

export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0]!;
}
